'use client';

import { getIcon } from 'phosphor-icons/getIcon';
import { DynamicIcon } from 'phosphor-icons/react';
import { useEffect, useState, type ReactNode } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Tooltip, TooltipArrow, useTooltip } from '../../atoms/Tooltip';

type TruncatedLabelWithTooltipProps = {
  label: string;
  children?: ReactNode;
};

const TruncatedLabelWithTooltip = ({
  label,
  children,
}: TruncatedLabelWithTooltipProps) => {
  const {
    context,
    isOpen,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    arrowRef,
  } = useTooltip({ offset: 4, arrow: true });

  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    if (refs.reference.current) {
      const element = refs.reference.current as HTMLElement;
      setIsTextTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [refs.reference]);

  return (
    <>
      <p
        className={c(
          'line-clamp-1 w-full min-w-0 overflow-hidden text-ellipsis',
          'whitespace-nowrap text-left text-xs leading-5 tracking-tight text-grey-400'
        )}
        ref={refs.setReference}
        {...(isTextTruncated ? getReferenceProps() : {})}
      >
        {children ?? label}
      </p>
      {isTextTruncated && isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="shadow-2xl"
          {...getFloatingProps()}
        >
          <TooltipArrow arrowRef={arrowRef} context={context} />
          <Tooltip
            label={label}
            className="max-w-40 break-words rounded px-4 py-3 text-sm font-normal leading-5 md:max-w-60"
          />
        </div>
      )}
    </>
  );
};

type HelpTextPart =
  | {
      type: 'text';
      value: string;
    }
  | {
      type: 'icon';
      iconName: string;
      weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone';
    };

const helpTextIconWeights = {
  Regular: 'regular',
  Fill: 'fill',
  Bold: 'bold',
  Duotone: 'duotone',
  Thin: 'thin',
  Light: 'light',
} as const;

const parseHelpText = (helpText: string): HelpTextPart[] => {
  const tokens = helpText.split(/\s+/).filter(Boolean);
  const parts: HelpTextPart[] = [];
  let textBuffer: string[] = [];

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    const iconComponent = getIcon(token);

    if (iconComponent) {
      if (textBuffer.length) {
        parts.push({ type: 'text', value: textBuffer.join(' ') });
        textBuffer = [];
      }

      const nextToken = tokens[index + 1];
      const weight =
        nextToken && nextToken in helpTextIconWeights
          ? helpTextIconWeights[nextToken as keyof typeof helpTextIconWeights]
          : undefined;

      if (weight) {
        index += 1;
      }

      parts.push({
        type: 'icon',
        iconName: token,
        weight,
      });

      continue;
    }

    textBuffer.push(token);
  }

  if (textBuffer.length) {
    parts.push({ type: 'text', value: textBuffer.join(' ') });
  }

  return parts.length ? parts : [{ type: 'text', value: helpText }];
};

export const getHelpTextLabel = (helpText: string) => {
  const isTextPart = (
    part: HelpTextPart
  ): part is { type: 'text'; value: string } => part.type === 'text';

  const label = parseHelpText(helpText)
    .filter(isTextPart)
    .map((part) => part.value)
    .join(' ')
    .trim();

  return label || helpText;
};

export const renderHelpText = (helpText: string) => {
  const parts = parseHelpText(helpText);
  const hasIcon = parts.some((part) => part.type === 'icon');
  const label = getHelpTextLabel(helpText);

  if (!hasIcon) {
    return <TruncatedLabelWithTooltip label={label} />;
  }

  return (
    <TruncatedLabelWithTooltip label={label}>
      <span className="inline-flex items-center gap-1">
        {parts.map((part) =>
          part.type === 'text' ? (
            <span key={`label-${part.type}`}>{part.value}</span>
          ) : (
            <DynamicIcon
              key={`icon-${part.iconName}`}
              iconName={part.iconName}
              size={10}
              className="text-grey-400"
              weight={part.weight ?? 'fill'}
              aria-hidden="true"
              focusable="false"
            />
          )
        )}
      </span>
    </TruncatedLabelWithTooltip>
  );
};
